import { formatNumber, useTranslation } from '@hbs-us/ui';

import { EvilAreaChart } from '../../../components/charts/charts/recharts-area-chart.js';
import type { ChartConfig } from '../../../components/charts/ui/recharts-chart.js';
import type { OverviewStats } from '../overviewTypes.js';

import { HomeCard } from './HomeCard.js';

/**
 * Revenue against cash collected, month by month.
 *
 * THIS USED TO DRAW ITS OWN SVG. It computed a viewBox, scaled every point by hand, built two
 * `<path d="…">` strings and placed the month labels with `text-anchor` guesses. It had no axes, no
 * hover, no tooltip, and its own private idea of what a chart looks like. The chart system that
 * shipped with the reports screens does all of that, so the hand-drawn version is gone.
 *
 * ⚠ COLOURS GO IN `config`, NEVER IN A CLASS. Tailwind v4 compiles `text-[--chart-3]` to invalid
 * CSS (`color: --chart-3`), which the browser drops — that is exactly how the production expenses
 * chart shipped with no bars. The chart resolves these tokens itself at paint time, and BRAND-06
 * stays satisfied because no hex reaches this file.
 *
 * ⚠ THE CHART IS `aria-hidden` AND THE NUMBERS FOLLOW IT IN A SCREEN-READER TABLE. A chart is not a
 * reliable information carrier on its own (A11Y-01), and hiding the graphic outright is what keeps
 * the axe run clean rather than leaving an unlabelled figure for it to flag.
 */

function monthLabel(month: string, locale: string): string {
  try {
    // UTC, like every other bare calendar value in this app: `2026-08` is a month, not an instant,
    // and letting the formatter apply a zone rolls it back to July west of UTC.
    return new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(
      new Date(`${month}-01T00:00:00Z`),
    );
  } catch {
    return month;
  }
}

export function RevenueTrendCard({
  stats,
  money,
}: {
  stats: OverviewStats;
  money: (value: number) => string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const series = stats.monthlySeries;

  // Built here, not as a module constant: the legend and the tooltip print `label` verbatim, so a
  // hardcoded English key ships "revenue" in lowercase to a Spanish reader.
  const config = {
    revenue: {
      label: t('home:trend.revenue'),
      colors: { light: ['var(--chart-3)'], dark: ['var(--chart-3)'] },
    },
    collected: {
      label: t('home:trend.collected'),
      colors: { light: ['var(--chart-5)'], dark: ['var(--chart-5)'] },
    },
  } satisfies ChartConfig;

  const data = series.map((point) => ({
    month: monthLabel(point.month, locale),
    revenue: Number(point.revenue || 0),
    collected: Number(point.collected || 0),
  }));

  // The headline sits over the "Last 6 months" label, so it is the window's total. It used to be
  // the latest month alone, which read "$0, last 6 months" over a chart peaking at tens of thousands.
  // Summed in integer cents: adding float dollars drifts.
  const windowRevenue =
    data.reduce((cents, point) => cents + Math.round(point.revenue * 100), 0) / 100;
  const latestRevenue = series[series.length - 1]?.revenue ?? stats.revenueMtd;
  const firstRevenue = series[0]?.revenue ?? latestRevenue;
  const delta = firstRevenue ? (latestRevenue - firstRevenue) / firstRevenue : 0;
  const deltaLabel = formatNumber(delta, locale, {
    style: 'percent',
    signDisplay: 'always',
    maximumFractionDigits: 1,
  });
  const caption = t('home:trend.caption', {
    latest: money(latestRevenue),
    delta: deltaLabel,
    months: series.length,
  });

  return (
    <HomeCard
      headingId="home-trend-heading"
      className="overflow-hidden xl:col-span-2"
      title={t('home:trend.heading')}
      description={t('home:trend.subheading')}
      action={
        <div className="shrink-0 text-right">
          <div className="font-display font-semibold text-3xl text-foreground tabular-nums">
            {money(windowRevenue)}
          </div>
          {/*
            The window used to be a pill with a caret on it, which read as a dropdown and answered
            no click. A control that cannot be operated is worse than a label, so it is a label.
          */}
          <div className="mt-1 text-muted-foreground text-xs">{t('home:trend.window')}</div>
        </div>
      }
      status={data.length === 0 ? 'empty' : 'ready'}
      emptyText={t('home:trend.empty')}
    >
      <div className="mt-3">
        <div aria-hidden="true">
          <EvilAreaChart
            className="h-60 w-full"
            config={config}
            data={data}
            curveType="monotone"
            xDataKey="month"
          >
            <EvilAreaChart.Grid />
            <EvilAreaChart.XAxis dataKey="month" />
            <EvilAreaChart.YAxis tickFormatter={(value: number) => money(value)} width={72} />
            <EvilAreaChart.Tooltip />
            <EvilAreaChart.Legend />
            {/*
              ⚠ BOTH PROPS ARE LOAD-BEARING. `Area` defaults to `strokeVariant="dashed"` at a
              0.8px stroke, which paints a hairline of dots you cannot see on a card — the chart
              mounted, the DOM was correct, and the screen read as empty grid lines. Revenue is
              the subject, so it gets the fill and a solid stroke. Collected is the comparison, so
              it keeps the dashed stroke and drops its fill: two gradient areas stacked over each
              other turn the overlap to mud and neither reads.
            */}
            <EvilAreaChart.Area dataKey="revenue" strokeVariant="solid" strokeWidth={2} />
            <EvilAreaChart.Area
              dataKey="collected"
              strokeVariant="dashed"
              strokeWidth={2}
              areaProps={{ dataKey: 'collected', fillOpacity: 0 }}
            />
          </EvilAreaChart>
        </div>

        {/* sr-only on the wrapper: a table ignores the 1px width and stretched the page sideways on phones. */}
        <div className="sr-only">
          <table>
            <caption>{caption}</caption>
            <thead>
              <tr>
                <th scope="col">{t('home:trend.month')}</th>
                <th scope="col">{t('home:trend.revenue')}</th>
                <th scope="col">{t('home:trend.collected')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((point) => (
                <tr key={point.month}>
                  <th scope="row">{point.month}</th>
                  <td>{money(point.revenue)}</td>
                  <td>{money(point.collected)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </HomeCard>
  );
}
